import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";

/**
 * Catch-all 404. Reuses the Home hero's panel treatment — rounded gradient
 * card with two glow circles orbiting its edge — so a wrong URL still lands on
 * something that looks like the rest of the site.
 */
const NotFound = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Page Not Found | Zero Typing AI</title>
        <meta name="description" content="The page you're looking for doesn't exist." />
        {/* A 404 should never be indexed. */}
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="bg-white md:p-4 p-3 flex flex-col max-w-screen-2xl mx-auto leading-tight">
        <Navbar />

        <div className="relative overflow-hidden rounded-2xl mt-6 md:mt-0">
          {/*
            Below md the Home hero drops the animated layer entirely, so the
            panel gets the same gradient without the orbiting circles.
          */}
          <div
            className="absolute inset-0 z-0 rounded-2xl bg-gradient-to-t from-[#3AB1F650] to-[#D8EFFD] md:hidden"
            aria-hidden="true"
          />

          <div
            className="absolute top-0 left-0 w-full h-full hidden md:flex flex-col z-0"
            aria-hidden="true"
          >
            <div className="relative flex-[1] bg-gradient-to-t from-[#3AB1F650] to-[#D8EFFD] rounded-2xl">
              <svg
                className="absolute top-0 left-0 w-full h-full rounded-2xl z-[4]"
                viewBox="0 0 1000 1350"
                preserveAspectRatio="none"
              >
                <path
                  id="notFoundOrbitPath"
                  d="
              M40,0
              H960
              A40,40 0 0 1 1000,40
              V1310
              A40,40 0 0 1 960,1350
              H40
              A40,40 0 0 1 0,1310
              V40
              A40,40 0 0 1 40,0
              Z"
                  fill="none"
                  stroke="transparent"
                />

                {/* Orbiting glow circles */}
                <circle
                  r="300"
                  fill="url(#notFoundGlow)"
                  filter="url(#notFoundBlur)"
                >
                  <animateMotion
                    repeatCount="indefinite"
                    dur="20s"
                    rotate="auto"
                  >
                    <mpath href="#notFoundOrbitPath" />
                  </animateMotion>
                </circle>

                <circle
                  r="300"
                  fill="url(#notFoundGlow)"
                  filter="url(#notFoundBlur)"
                >
                  <animateMotion
                    repeatCount="indefinite"
                    dur="20s"
                    rotate="auto"
                    keyPoints="1;0"
                    keyTimes="0;1"
                  >
                    <mpath href="#notFoundOrbitPath" />
                  </animateMotion>
                </circle>

                <defs>
                  <radialGradient id="notFoundGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#3AB1F6" stopOpacity=".9" />
                    <stop offset="100%" stopColor="#3AB1F6" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <filter
                  id="notFoundBlur"
                  x="-100%"
                  y="-100%"
                  width="300%"
                  height="300%"
                >
                  <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
                </filter>
              </svg>
            </div>
          </div>

          {/* Content — vertically centred in whatever height is left below the navbar */}
          <div className="relative z-10 flex min-h-[560px] md:min-h-[calc(100vh-32px)] flex-col items-center justify-center px-6 pt-28 md:pt-36 pb-16 md:pb-24 text-center">
            <p className="font-jakarta font-bold tracking-[-0.04em] text-[#0A0D16] text-[96px] sm:text-[140px] md:text-[180px] lg:text-[210px] leading-[0.85]">
              404
            </p>

            <h1 className="mt-[24px] font-jakarta font-bold tracking-[-0.8px] text-[#0A0D16] text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.2]">
              Looks like we lost the words.
            </h1>

            <p className="mt-[18px] max-w-[560px] font-inter text-[15px] sm:text-[16px] lg:text-[18px] font-normal leading-[1.7] tracking-[0px] text-[#0A0D1699]">
              The page you&apos;re looking for doesn&apos;t exist&mdash;or it
              decided to stop listening.
            </p>

            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-[30px] cursor-pointer rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] px-8 py-3.5 font-inter text-[15px] sm:text-[16px] font-semibold text-white shadow-[0_4px_18px_rgba(58,177,246,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:from-[#33A2E5] hover:to-[#386FE8] hover:shadow-xl"
            >
              Go back Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default NotFound;
